import { clip, type Clip } from '../clip.ts';
import { TAU, inCubic, inOut, outCubic, outExpo, outQuad, phase, settle } from '../curves.ts';
import { timeline, type KeyPose } from '../pose.ts';
import { sentinelClips } from './sentinel.ts';

/*
 * Ser Edda, the Sworn (models/edda.ts, docs/ANTIHEROES.md): on the fallen sentinel's bones like Ser Corvin, so she
 * walks, reels, staggers and falls as it does. Her own: the mace brought down overhead (attack, landing at 0.13 s),
 * judgement (the mace lifted to the sky, then levelled at a hero), the aegis (the shield raised high), the channel of
 * the Fallen Chapel (mace and shield up, her head back), the Chapel itself (the mace driven down, 0.2 s) and the oath
 * broken (she drops to a knee behind her shield for as long as it holds, about 4 s).
 *
 * Conventions as in sentinel.ts: −X on an arm swings it forward and up, −X on a forearm bends the elbow, +X on
 * the trunk leans it forward, +Y turns the trunk to its left.
 */

type B =
  | 'root' | 'body' | 'chest' | 'head' | 'flapF' | 'flapB'
  | 'armL' | 'foreL' | 'shield' | 'armR' | 'foreR' | 'sword'
  | 'legL' | 'shinL' | 'legR' | 'shinR';
type P = KeyPose<B>;

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

const UP: P = { root: [0, 0.08, 0, 0, -0.02, -0.04], body: [-0.06, -0.15, 0], chest: [-0.15, -0.2, 0], head: [0, 0.15, 0], armR: [-2.8, 0, -0.2], foreR: [-0.6, 0, 0], sword: [-0.4, 0, 0], armL: [0.2, 0, 0.1], legL: [-0.2, 0, -0.05], shinL: [0.2, 0, 0], legR: [0.25, 0, 0.05], shinR: [0.3, 0, 0] };
const DOWN: P = { root: [0, -0.12, 0, 0, -0.06, 0.14], body: [0.2, 0.15, 0], chest: [0.3, 0.25, 0], head: [0.1, -0.2, 0], armR: [-0.5, 0, 0.15], foreR: [-0.1, 0, 0], sword: [0.5, 0, 0], armL: [0.15, 0, 0.15], legL: [-0.5, 0, -0.05], shinL: [0.45, 0, 0], legR: [0.35, 0, 0.05], shinR: [0.15, 0, 0], flapF: [-0.3, 0, 0], flapB: [0.25, 0, 0] };

/** Her blow: the mace up overhead and down. */
const attack = (): Clip<B> =>
  clip<B>('attack', 1.1, false, (b, t) => {
    timeline(b, t, [
      [0, UP],
      [0.05, { ...UP, armR: [-2.95, 0, -0.2] }, outQuad],
      [0.13, DOWN, inCubic],
      [0.4, DOWN, outCubic],
      [1.1, {}, inOut],
    ], { head: 0.04, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0, 0, -0.02 * kick(t, 0.13, 6, 9));
  }, (t) => thud(t, [[0.13, 0.04]]), 'Maza');

/** Judgement: the mace lifted to the sky, then levelled at the hero it falls on. */
const judgement = (): Clip<B> =>
  clip<B>('judgement', 1.3, false, (b, t) => {
    const sky: P = { body: [-0.1, 0, 0], chest: [-0.2, 0, 0], head: [-0.4, 0, 0], armR: [-3.0, 0, -0.05], foreR: [0, 0, 0], sword: [0, 0, 0], armL: [0.25, 0, 0.15] };
    const point: P = { body: [0, 0.15, 0], chest: [-0.02, 0.3, 0], head: [-0.05, -0.25, 0], armR: [-1.6, 0, 0.05], foreR: [0, 0, 0], sword: [-1.2, 0, 0], armL: [0.3, 0, 0.15], legL: [-0.2, 0, 0], legR: [0.15, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.3, sky, outExpo],
      [0.5, point, inCubic],
      [0.95, point],
      [1.3, {}, inOut],
    ], { head: 0.05 });
  }, undefined, 'Juicio');

/** The aegis: the shield raised high before her, braced. */
const aegis = (): Clip<B> =>
  clip<B>('aegis', 1.4, false, (b, t) => {
    const up: P = { root: [0, -0.06, 0, 0, -0.06, 0], body: [0.05, 0.35, 0], chest: [0, 0.3, 0], head: [-0.1, -0.2, 0], armL: [-2.1, 0, 0.25], foreL: [-0.4, 0, 0], shield: [-0.2, 0, 0], armR: [-0.3, 0, -0.25], legL: [-0.35, 0, -0.08], shinL: [0.45, 0, 0], legR: [0.35, 0, 0.08], shinR: [0.4, 0, 0] };
    timeline(b, t, [
      [0, {}],
      [0.25, up, outExpo],
      [1.0, up],
      [1.4, {}, inOut],
    ], { head: 0.05 });
  }, (t) => thud(t, [[0.25, 0.03]]), 'Égida');

/** The Fallen Chapel, channelled: mace and shield raised, her head thrown back. */
const channel = (): Clip<B> =>
  clip<B>('channel', 1.2, true, (b, t) => {
    const p = phase(t, 1.2) * TAU;
    const shake = Math.sin(p * 7) * 0.03;
    b.root.pos(0, -0.03 + 0.01 * Math.sin(p * 2), 0).rot(-0.05, 0, 0);
    b.body.rot(-0.1, 0, 0);
    b.chest.rot(-0.22 + shake, 0, 0);
    b.head.rot(-0.45, 0, 0);
    b.armR.rot(-3.0 + shake, 0, -0.1);
    b.foreR.rot(-0.05);
    b.sword.rot(-0.1 + shake * 2);
    b.armL.rot(-1.2, 0, 0.4);
    b.foreL.rot(-0.5);
    b.legL.rot(-0.2, 0, -0.08);
    b.shinL.rot(0.25);
    b.legR.rot(0.2, 0, 0.08);
    b.shinR.rot(0.3);
    b.flapF.rot(0.1 * Math.sin(p * 3));
    b.flapB.rot(-0.12 + 0.1 * Math.sin(p * 3 + 1));
  }, (t) => {
    const k = Math.sin(phase(t, 1.2) * TAU * 7) * 0.006;
    return [1 + k, 1 - k, 1 + k];
  }, 'Canaliza');

/** The Fallen Chapel lands: the mace driven down into the ground. */
const pillar = (): Clip<B> =>
  clip<B>('pillar', 1.6, false, (b, t) => {
    const high: P = { chest: [-0.22, 0, 0], head: [-0.45, 0, 0], armR: [-3.0, 0, -0.1], foreR: [-0.05, 0, 0], sword: [-0.1, 0, 0], armL: [-1.2, 0, 0.4], foreL: [-0.5, 0, 0] };
    const down: P = { root: [0, 0, 0, 0, -0.2, 0.2], body: [0.35, 0, 0], chest: [0.4, 0, 0], head: [0.2, 0, 0], armR: [-0.6, 0, 0.1], foreR: [-0.05, 0, 0], sword: [0.9, 0, 0], armL: [0.2, 0, 0.3], legL: [-0.8, 0, -0.05], shinL: [1.0, 0, 0], legR: [0.55, 0, 0.05], shinR: [0.6, 0, 0], flapF: [-0.5, 0, 0], flapB: [0.45, 0, 0] };
    timeline(b, t, [
      [0, high],
      [0.2, down, inCubic],
      [0.85, down],
      [1.6, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
    b.root.pos(0.02 * kick(t, 0.2, 7, 6), 0, 0);
  }, (t) => thud(t, [[0.2, 0.09]]), 'La Capilla Caída');

/** The oath broken: down on one knee behind her shield, head bowed, as long as it holds; then up. */
const oath = (): Clip<B> =>
  clip<B>('oath', 4.2, false, (b, t) => {
    const kneel: P = { root: [0, 0, 0, 0, -0.42, 0], body: [0.2, 0.1, 0], chest: [0.25, 0.1, 0], head: [0.45, 0, 0], legL: [-1.5, 0, -0.05], shinL: [1.5, 0, 0], legR: [0.2, 0, 0.08], shinR: [1.9, 0, 0], armL: [-1.1, 0, 0.3], foreL: [-0.6, 0, 0], shield: [0.1, 0, 0], armR: [-0.4, 0, -0.1], foreR: [-0.3, 0, 0], sword: [1.0, 0, 0], flapF: [-0.6, 0, 0], flapB: [0.5, 0, 0] };
    const breath = 0.02 * Math.sin(t * 3);
    timeline(b, t, [
      [0, {}],
      [0.3, kneel, outExpo],
      [3.7, { ...kneel, chest: [0.25 + breath, 0.1, 0] }],
      [4.2, {}, inOut],
    ], { head: 0.05, flapF: 0.08, flapB: 0.08 });
  }, (t) => thud(t, [[0.3, 0.05]]), 'Juramento roto');

/** Clips by library kind (see ../rigs.ts): the sentinel's, with her own blow and powers in place of the bash. */
export const eddaClips = (): Clip[] => [...sentinelClips().filter((c) => c.name !== 'attack'), attack(), judgement(), aegis(), channel(), pillar(), oath()] as Clip[];
export const CLIPS: Record<string, () => Clip[]> = { edda: eddaClips };
